import { env } from "@/config/env";

const API_PREFIX = "/drt/";
const SUPPORT_INTERNAL_PREFIX = "support/internal/";

const trimLeadingSlash = (value: string): string => value.replace(/^\/+/, "");

/**
 * Removes 'support/internal/' prefix from path if present
 * This prevents duplication when the authentication proxy adds it back
 */
const removeSupportInternalPrefix = (path: string): string => {
  const trimmed = trimLeadingSlash(path);
  if (trimmed.startsWith(SUPPORT_INTERNAL_PREFIX)) {
    return trimmed.substring(SUPPORT_INTERNAL_PREFIX.length);
  }
  return trimmed;
};

export const apiRoutes = {
  // Auth is BFF-owned: the Dispatcher BFF serves login/session/logout under
  // /drt/api/bff/* directly (no longer proxied to RoR, which dropped those
  // endpoints in Phase 2). Everything else (forgotPassword/proxy/upload/
  // download/feedback) still flows through the BFF proxy to RoR/K3 on /drt/api/*.
  //
  // Blast-radius note (SHARED — apiRoutes is imported by ~100+ files):
  //   login, session, logout are all on /drt/api/bff/* — served directly
  //   by the BFF. Do NOT remove the /bff/ prefix; RoR dropped these endpoints
  //   in Phase 2 so /drt/api/login and /drt/api/session would return 404.
  login: (): string => `${API_PREFIX}api/bff/login`,
  forgotPassword: (): string => `${API_PREFIX}api/bff`,
  session: (): string => `${API_PREFIX}api/bff/session`,
  logout: (): string => `${API_PREFIX}api/bff/logout`,
  feedback: (): string => `${API_PREFIX}api/bff/feedback/send`,
  /**
   * BFF (Backend-for-Frontend) routes — called directly on the BFF layer,
   * NOT proxied through `support/internal/`.
   *
   * Blast-radius note (shared — apiRoutes is imported by ~100+ files):
   *   Consumers:
   *     - `useCreateRoundTrip`        → `POST /drt/api/bff/round-trips`
   *     - `fetchGeoSession`           → `POST /drt/api/bff/geo-session`
   *     - `fetchConvAiSession`        → `POST /drt/api/bff/conv-ai-session` (interim Conv-AI auth key)
   *     - `createSelfServiceAccountViaBff` → `POST /drt/api/bff/selfservice/accounts` (ECOPD-10071)
   *   Do NOT use `bff()` for K3 select_query / proxy calls — use `apiRoutes.proxy()` for those.
   *   Any new BFF endpoint must be added to this note.
   */
  bff: (path: string): string => `${API_PREFIX}api/bff/${trimLeadingSlash(path)}`,
  /**
   * BFF ODS endpoints — GET /drt/api/bff/ods/<segment>.
   *
   * Read-only historical dashboard reads served by the Dispatcher BFF from the
   * Databricks ODS (tier-2, ~1h stale), gated by OdsAccessGuard (session + tenant).
   * Used by the fleet-dashboard ODS fetchers when the tenant flips
   * `supervisor.dashboard.ods.enabled` on (see useFleetDashboardApi / createFleetDashboardApi);
   * otherwise the widgets use the direct-K3 `*Live.ts` path.
   *
   *   apiRoutes.bffOds("checklist-failures") // → "/drt/api/bff/ods/checklist-failures"
   */
  bffOds: (segment: string): string =>
    `${API_PREFIX}api/bff/ods/${trimLeadingSlash(segment)}`,
  proxy: (path: string): string => {
    const cleanPath = removeSupportInternalPrefix(path);
    return `${API_PREFIX}api/proxy/${cleanPath}`;
  },
  upload: (path: string): string => {
    const cleanPath = removeSupportInternalPrefix(path);
    return `${API_PREFIX}api/upload/${cleanPath}`;
  },
  download: (path: string): string => {
    const cleanPath = removeSupportInternalPrefix(path);
    return `${API_PREFIX}api/download/${cleanPath}`;
  },
  /**
   * Real-time vehicle position SSE endpoint.
   * tenantId is the logged-in user's tenant (from useTenantContext).
   */
  telemetrySse: (tenantId: string): string =>
    `${env.TELEMETRY_SSE_BASE_URL}/runtime/sse/tenant?tenantId=${encodeURIComponent(tenantId)}`,

  /**
   * Maps Proxy Service routes — ECOPD-15205.
   * All map data calls (geocoding, autocomplete, routing) go through the BE
   * proxy instead of hitting Google directly.  Tile rendering via the Google
   * Maps JS SDK is NOT affected.
   *
   * Base: /api/v1/  (served by maps-proxy-service, forwarded by BFF)
   */
  maps: {
    /** POST /api/v1/geocoding/search — forward geocode */
    geocodingSearch: (): string => '/api/v1/geocoding/search',

    /** GET /api/v1/geocoding/reverse?lat=&lon=&limit=&language= — reverse geocode */
    geocodingReverse: (
      lat: number,
      lon: number,
      limit = 1,
      language = 'en',
    ): string =>
      `/api/v1/geocoding/reverse?lat=${lat}&lon=${lon}&limit=${limit}&language=${language}`,

    /** GET /api/v1/geocoding/search?q=&countryCode=&limit=&language= — text address search */
    geocodingAddressSearch: (
      q: string,
      countryCode: string,
      limit = 5,
      language = 'en',
    ): string =>
      `/api/v1/geocoding/search?q=${encodeURIComponent(q)}&countryCode=${countryCode}&limit=${limit}&language=${language}`,

    /** POST /api/v1/geocoding/autocomplete — typeahead predictions */
    geocodingAutocomplete: (): string => '/api/v1/geocoding/autocomplete',

    /** POST /api/v1/geocoding/place-search — POI / business name search */
    geocodingPlaceSearch: (): string => '/api/v1/geocoding/place-search',

    /** POST /api/v1/routing/directions — replaces DirectionsService */
    routingDirections: (): string => '/api/v1/routing/directions',

    /** POST /api/v1/routing/eta — single travel-time estimate */
    routingEta: (): string => '/api/v1/routing/eta',

    /** POST /api/v1/routing/matrix — distance matrix for N×M pairs */
    routingMatrix: (): string => '/api/v1/routing/matrix',

    /** GET /api/v1/routing?from=lat,lon&to=lat,lon&provider= — legacy compat */
    routingLegacy: (
      from: string,
      to: string,
      provider = 'OSRM',
    ): string => `/api/v1/routing?from=${from}&to=${to}&provider=${provider}`,
  },
};
